import { useMemo, useState } from "react";
import { DownloadIcon, InfoIcon } from "lucide-react";
import type { PayrollUiEmployee, PayrollUiPayPeriod } from "../../types";

type TimeEntryGridProps = {
  employees: PayrollUiEmployee[];
  period: PayrollUiPayPeriod;
};

export function TimeEntryGrid({ employees, period }: TimeEntryGridProps) {
  const hourlyEmployees = employees.filter(
    (employee) => employee.employmentType === "Hourly",
  );
  const startDate = new Date(period.startDate);
  const endDate = new Date(period.endDate);
  const days: { date: Date; isHoliday: boolean }[] = [];

  for (
    let day = new Date(startDate);
    day <= endDate;
    day.setDate(day.getDate() + 1)
  ) {
    const isHoliday =
      period.hasHoliday && (day.getDate() === 12 || day.getDate() === 13 || day.getDate() === 21);
    days.push({
      date: new Date(day),
      isHoliday,
    });
  }

  const initialHours = useMemo(
    () =>
      hourlyEmployees.reduce<Record<string, Record<string, number>>>(
        (employeeMap, employee) => {
          employeeMap[employee.id] = employee.timeEntries
            .filter((entry) => entry.periodId === period.id)
            .reduce<Record<string, number>>((entryMap, entry) => {
              entryMap[entry.date] = entry.hours;
              return entryMap;
            }, {});
          return employeeMap;
        },
        {},
      ),
    [hourlyEmployees, period.id],
  );

  const [hours, setHours] =
    useState<Record<string, Record<string, number>>>(initialHours);

  const handleHourChange = (employeeId: string, dateStr: string, value: string) => {
    const numVal = parseFloat(value);
    if (Number.isNaN(numVal) && value !== "") {
      return;
    }
    setHours((previous) => ({
      ...previous,
      [employeeId]: {
        ...(previous[employeeId] || {}),
        [dateStr]: value === "" ? 0 : numVal,
      },
    }));
  };

  const getEmployeeTotal = (employeeId: string) => {
    const employeeHours = hours[employeeId] || {};
    return Object.values(employeeHours).reduce((sum, value) => sum + (value || 0), 0);
  };

  const getEmployeeGross = (employee: PayrollUiEmployee) => {
    const totalHours = getEmployeeTotal(employee.id);
    const rate =
      employee.positions.find((position) => position.compensationType === "Hourly Rate")?.amount ?? 0;
    return totalHours * rate;
  };

  const getDayTotal = (dateStr: string) =>
    hourlyEmployees.reduce((sum, employee) => {
      return sum + ((hours[employee.id] || {})[dateStr] || 0);
    }, 0);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);

  return (
    <div className="flex flex-col overflow-hidden rounded-[12px] border border-border bg-surface-light shadow-sm">
      <div className="flex items-center justify-between border-b border-border bg-muted/30 p-4">
        <h2 className="font-title text-lg font-semibold text-iron-grey">
          Hourly Time Entry
        </h2>
        <button className="flex items-center gap-2 rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-[13px] font-medium text-iron-grey shadow-sm transition-colors hover:bg-muted">
          <DownloadIcon className="h-4 w-4 text-text-secondary" />
          Import Hours
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-border bg-muted">
              <th className="sticky left-0 z-10 min-w-[180px] border-r border-border bg-muted px-4 py-3 font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                Employee
              </th>
              {days.map((day) => (
                <th
                  key={day.date.toISOString()}
                  className={`min-w-[60px] px-2 py-3 text-center font-body text-xs font-medium uppercase tracking-wider ${
                    day.isHoliday
                      ? "bg-warning-background text-coral"
                      : "text-text-secondary"
                  }`}
                >
                  <div className="flex flex-col items-center gap-1">
                    <span>
                      {day.date.toLocaleDateString("en-US", {
                        weekday: "short",
                      })}
                    </span>
                    <span>{day.date.getDate()}</span>
                    {day.isHoliday ? (
                      <div className="group relative mt-1 cursor-help">
                        <InfoIcon className="h-3.5 w-3.5 text-coral" />
                        <div className="absolute bottom-full left-1/2 z-50 mb-2 hidden w-48 -translate-x-1/2 rounded bg-iron-grey p-2 text-xs text-white shadow-lg group-hover:block normal-case">
                          {period.holidayName}: {period.holidayRule}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </th>
              ))}
              <th className="sticky right-[100px] z-10 min-w-[80px] border-l border-border bg-muted px-4 py-3 text-right font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                Total Hrs
              </th>
              <th className="sticky right-0 z-10 min-w-[100px] bg-muted px-4 py-3 text-right font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                Gross Pay
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {hourlyEmployees.map((employee) => (
              <tr key={employee.id} className="transition-colors hover:bg-[#fbf9fa]">
                <td className="sticky left-0 z-10 border-r border-border bg-surface-light px-4 py-3 font-body text-sm font-medium text-iron-grey">
                  {employee.lastName}, {employee.firstName}
                  <div className="font-normal text-xs text-text-secondary">
                    {employee.employeeId}
                  </div>
                </td>
                {days.map((day) => {
                  const dateStr = day.date.toISOString().split("T")[0] ?? "";
                  return (
                    <td
                      key={`${employee.id}-${dateStr}`}
                      className={`px-1 py-2 ${
                        day.isHoliday ? "bg-warning-background/30" : ""
                      }`}
                    >
                      <input
                        type="number"
                        min="0"
                        max="24"
                        step="0.25"
                        value={(hours[employee.id] || {})[dateStr] || ""}
                        onChange={(event) =>
                          handleHourChange(employee.id, dateStr, event.target.value)
                        }
                        className="w-full rounded-[4px] border border-transparent bg-transparent px-1 py-1.5 text-center font-body text-sm text-iron-grey transition-all hover:border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </td>
                  );
                })}
                <td className="sticky right-[100px] z-10 border-l border-border bg-surface-light px-4 py-3 text-right font-body text-sm font-semibold text-iron-grey">
                  {getEmployeeTotal(employee.id).toFixed(2)}
                </td>
                <td className="sticky right-0 z-10 bg-surface-light px-4 py-3 text-right font-body text-sm font-semibold text-primary">
                  {formatCurrency(getEmployeeGross(employee))}
                </td>
              </tr>
            ))}
            {hourlyEmployees.length === 0 ? (
              <tr>
                <td
                  colSpan={days.length + 3}
                  className="px-4 py-12 text-center font-body text-sm text-text-secondary"
                >
                  No hourly employees found for this period.
                </td>
              </tr>
            ) : null}
            {hourlyEmployees.length > 0 ? (
              <tr className="border-t-2 border-border-strong bg-muted font-semibold">
                <td className="sticky left-0 z-10 border-r border-border bg-muted px-4 py-3 font-body text-sm text-iron-grey">
                  Daily Totals
                </td>
                {days.map((day) => {
                  const dateStr = day.date.toISOString().split("T")[0] ?? "";
                  return (
                    <td
                      key={`total-${dateStr}`}
                      className="px-2 py-3 text-center font-body text-sm text-iron-grey"
                    >
                      {getDayTotal(dateStr) > 0
                        ? getDayTotal(dateStr).toFixed(2)
                        : "-"}
                    </td>
                  );
                })}
                <td className="sticky right-[100px] z-10 border-l border-border bg-muted px-4 py-3 text-right font-body text-sm text-iron-grey">
                  {hourlyEmployees
                    .reduce((sum, employee) => sum + getEmployeeTotal(employee.id), 0)
                    .toFixed(2)}
                </td>
                <td className="sticky right-0 z-10 bg-muted px-4 py-3 text-right font-body text-sm text-primary">
                  {formatCurrency(
                    hourlyEmployees.reduce(
                      (sum, employee) => sum + getEmployeeGross(employee),
                      0,
                    ),
                  )}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
